/*
 * מחולל מספרים פסאודו-אקראיים דטרמיניסטי — sfc32, על uint32 בלבד.
 *
 * Math.random אסור בכל הקוד הטהור: בלעדיו אין שחזור, אין replay, אין טסט דטרמיניזם,
 * ואין מולטיפלייר. src/sim/purity.test.ts אוכף את האיסור.
 *
 * שני זרמים, בכוונה:
 *   1. זרם העולם — נשמר בתוך המצב (RngState) ומתקדם עם הסימולציה.
 *   2. deriveRng(seed, player, tick) — חסר מצב לגמרי. ה-AI מגריל ממנו בלי לגעת
 *      בזרם העולם, ולכן שחזור משמירה מחזיר גם את ההתנהגות של ה-AI בחינם.
 */

export interface RngState {
    a: number;
    b: number;
    c: number;
    d: number;
}

export function createRng(seed: number): RngState {
    // הרחבת seed בודד לארבעה מצבים, ואז חימום כדי לא להתחיל מדפוס מנוון.
    const state: RngState = {
        a: seed >>> 0,
        b: (seed ^ 0x9e3779b9) >>> 0,
        c: (seed ^ 0x85ebca6b) >>> 0,
        d: (seed ^ 0xc2b2ae35) >>> 0,
    };
    for (let i = 0; i < 12; i++) nextUint32(state);
    return state;
}

export function cloneRng(state: RngState): RngState {
    return { a: state.a, b: state.b, c: state.c, d: state.d };
}

/** מקדם את המצב ומחזיר uint32. זו הפעולה היחידה שמשנה את הזרם. */
export function nextUint32(state: RngState): number {
    const t = (state.a + state.b) >>> 0;
    state.a = (state.b ^ (state.b >>> 9)) >>> 0;
    state.b = (state.c + (state.c << 3)) >>> 0;
    state.c = ((state.c << 21) | (state.c >>> 11)) >>> 0;
    state.c = (state.c + ((t + state.d) >>> 0)) >>> 0;
    state.d = (state.d + 0x9e3779b9) >>> 0;
    return (state.c + t) >>> 0;
}

/** שלם אחיד ב-[0, bound). bound חייב להיות חיובי. */
export function nextInt(state: RngState, bound: number): number {
    if (bound <= 1) return 0;
    // דחיית ערכים מהזנב, כדי שההתפלגות תישאר אחידה בדיוק ולא רק בקירוב.
    const limit = (0x100000000 - (0x100000000 % bound)) >>> 0;
    for (;;) {
        const value = nextUint32(state);
        if (value < limit || limit === 0) return value % bound;
    }
}

/** שלם אחיד ב-[min, max] כולל. */
export function nextRange(state: RngState, min: number, max: number): number {
    return min + nextInt(state, max - min + 1);
}

/** מגריל true בהסתברות של percent אחוזים (שלם 0..100). */
export function nextChance(state: RngState, percent: number): boolean {
    if (percent <= 0) return false;
    if (percent >= 100) return true;
    return nextInt(state, 100) < percent;
}

function mix32(value: number): number {
    let x = value >>> 0;
    x = Math.imul(x ^ (x >>> 16), 0x7feb352d) >>> 0;
    x = Math.imul(x ^ (x >>> 15), 0x846ca68b) >>> 0;
    return (x ^ (x >>> 16)) >>> 0;
}

/**
 * זרם נגזר וחסר-מצב: אותם (seed, player, tick) תמיד נותנים אותו זרם, בלי קשר למה
 * שקרה בעולם. זה מה שמאפשר להחליף שחקן AI במקור פקודות מהרשת בלי ששום דבר אחר ישתנה.
 */
export function deriveRng(seed: number, player: number, tick: number): RngState {
    return createRng(mix32(mix32(seed ^ 0x1b873593) ^ Math.imul(player + 1, 0x27d4eb2f) ^ tick));
}
